import axios from 'axios';
import BrowserNotificationService from './browserNotificationService';

function getNotificationsBaseUrl(): string {
    const base = (axios.defaults.baseURL || '').toString().replace(/\/$/, '');
    return base ? `${base}/api/notifications` : '/api/notifications';
}

interface Notification {
    id: string;
    title: string;
    message: string;
    category?: string;
    type?: string;
    action_url?: string;
    is_read: boolean;
    is_sent: boolean;
    created_at: string;
}

interface NotificationStats {
    total: number;
    unread: number;
    read: number;
    categories: Record<string, number>;
    types: Record<string, number>;
}

class NotificationPollingService {
    private static instance: NotificationPollingService;
    private pollingInterval: number = 900000; // 15 minutes
    private isPolling: boolean = false;
    private lastNotificationIds: Set<string> = new Set();
    private browserNotificationService: BrowserNotificationService;
    private retryCount: number = 0;
    private maxRetries: number = 5;
    private baseRetryDelay: number = 1000; // 1 second
    private isOnline: boolean = navigator.onLine;
    private notificationStats: NotificationStats | null = null;
    /** Last fetched notifications list for dropdown to avoid duplicate API calls */
    private lastFetchedNotifications: Notification[] = [];

    private constructor() {
        this.browserNotificationService = BrowserNotificationService.getInstance();
        this.setupOnlineOfflineListeners();
    }

    public static getInstance(): NotificationPollingService {
        if (!NotificationPollingService.instance) {
            NotificationPollingService.instance = new NotificationPollingService();
        }
        return NotificationPollingService.instance;
    }

    private setupOnlineOfflineListeners(): void {
        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('Network connection restored. Resuming notification polling.');
            if (this.isPolling) {
                this.fetchAndProcessNotifications();
            }
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('Network connection lost. Pausing notification polling.');
        });
    }

    public async startPolling(): Promise<void> {
        if (this.isPolling) {
            console.log('Notification polling already started');
            return;
        }

        console.log('Starting notification polling service...');

        // Request notification permission on start
        await this.browserNotificationService.requestPermission();

        this.isPolling = true;
        this.retryCount = 0;
        
        console.log('Notification polling started successfully');
        
        // Initial fetch to populate lastNotificationIds
        await this.fetchAndProcessNotifications();

        // Start polling
        this.poll();
    }

    public stopPolling(): void {
        this.isPolling = false;
        this.retryCount = 0;
    }

    private async poll(): Promise<void> {
        if (!this.isPolling) {
            return;
        }

        try {
            await this.fetchAndProcessNotifications();
            this.retryCount = 0; // Reset retry count on success
        } catch (error) {
            console.error('Error in notification polling:', error);
            this.handlePollingError();
        }

        // Calculate next poll interval (exponential backoff for errors)
        const nextInterval = this.retryCount > 0 
            ? Math.min(this.pollingInterval * Math.pow(2, this.retryCount - 1), 300000) // Max 5 minutes
            : this.pollingInterval;

        // Schedule next poll
        setTimeout(() => this.poll(), nextInterval);
    }

    private handlePollingError(): void {
        this.retryCount++;
        if (this.retryCount >= this.maxRetries) {
            console.warn('Max retry attempts reached. Stopping notification polling.');
            this.stopPolling();
        }
    }

    private async fetchAndProcessNotifications(): Promise<void> {
        if (!this.isOnline) {
            console.log('Skipping notification fetch - offline');
            return;
        }

        try {
            const authUser = localStorage.getItem('authUser');
            let token = '';
            if (authUser) {
                try {
                    // The token is stored as JSON.stringify(string), so we need to parse it
                    token = JSON.parse(authUser);
                } catch (error) {
                    console.warn('Failed to parse auth token in notification service:', error);
                    // Try using the raw value as fallback
                    token = authUser;
                }
            }
            
            console.log('Fetching notifications with token:', token ? 'Token exists' : 'No token');
            
            const url = `${getNotificationsBaseUrl()}/?page=1&per_page=50`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const text = await response.text();
                if (!text || text.trim().startsWith('<')) {
                    console.warn('Notifications API returned non-JSON (e.g. HTML). Skipping this poll.');
                    return;
                }
                let data: { data?: { notifications?: Notification[]; pagination?: unknown } };
                try {
                    data = JSON.parse(text);
                } catch (parseError) {
                    console.warn('Notifications API response is not valid JSON. Skipping this poll.');
                    return;
                }
                const notifications = data.data?.notifications ?? [];
                this.lastFetchedNotifications = Array.isArray(notifications) ? notifications : [];

                if (notifications.length > 0) {
                    const isInitialLoad = this.lastNotificationIds.size === 0;
                    await this.processNewNotifications(notifications, isInitialLoad);
                }

                if (data.data?.pagination) {
                    this.updateNotificationStats(data.data);
                }

                // Notify dropdown to sync from getLastFetchedNotifications() (avoids duplicate API)
                this.dispatchNotificationUpdateEvent(this.lastFetchedNotifications.length);
            } else if (response.status === 401 || response.status === 403) {
                console.warn('Authentication error in notification polling. Stopping polling.');
                this.stopPolling();
            } else {
                const text = await response.text().catch(() => '');
                if (text.trim().startsWith('<')) {
                    console.warn('Notifications API returned HTML instead of JSON (check API URL). Skipping.');
                    return;
                }
                console.error('Notification API error:', response.status, response.statusText);
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            throw error;
        }
    }

    private async processNewNotifications(notifications: Notification[], isInitialLoad = false): Promise<void> {
        const newNotifications: Array<{ title: string; options?: NotificationOptions }> = [];
        let hasLoginNotification = false;

        for (const notification of notifications) {
            // Check if this is a new notification
            if (!this.lastNotificationIds.has(notification.id)) {
                this.lastNotificationIds.add(notification.id);

                // On initial load (e.g. dashboard just opened), only seed IDs — do not show browser notifications for existing items (avoids "Login Successful" on every visit).
                if (isInitialLoad) continue;

                // Check if this is a login/session confirmation (for dropdown sync event only)
                const isLoginConfirmation =
                    notification.type === 'login_alert' ||
                    (notification.title && notification.title.toLowerCase().includes('login successful'));
                if (isLoginConfirmation) {
                    hasLoginNotification = true;
                    // Do not show browser notification for login confirmation — user is already in the app; show only in notification list.
                    continue;
                }

                // Only show browser notification for unread notifications (non–login types)
                if (!notification.is_read) {
                    const notificationOptions: NotificationOptions = {
                        body: notification.message,
                        tag: notification.id, // Prevent duplicate notifications
                        data: {
                            notificationId: notification.id,
                            actionUrl: notification.action_url,
                            category: notification.category,
                            type: notification.type
                        },
                        icon: '/favicon.png',
                        badge: '/favicon.png',
                        requireInteraction: notification.type === 'urgent' || notification.type === 'urgent_reminder',
                        silent: false
                    };

                    // Do not add actions: they are only supported via ServiceWorkerRegistration.showNotification()

                    newNotifications.push({
                        title: notification.title,
                        options: notificationOptions
                    });
                }
            }
        }

        // Show browser notifications for new unread notifications
        if (newNotifications.length > 0) {
            if (newNotifications.length === 1) {
                await this.browserNotificationService.showNotification(
                    newNotifications[0].title,
                    newNotifications[0].options
                );
            } else {
                await this.browserNotificationService.showMultipleNotifications(newNotifications);
            }

            // Dispatch custom event for notification updates
            this.dispatchNotificationUpdateEvent(newNotifications.length);
            
            // If we have a login notification, dispatch a special event
            if (hasLoginNotification) {
                this.dispatchLoginNotificationEvent();
            }
        }
    }

    private updateNotificationStats(data: any): void {
        this.notificationStats = {
            total: data.pagination.total,
            unread: data.pagination.total - (data.pagination.total - data.pagination.unread || 0),
            read: data.pagination.total - data.pagination.unread || 0,
            categories: data.categories || {},
            types: data.types || {}
        };
    }

    private dispatchNotificationUpdateEvent(count: number): void {
        const event = new CustomEvent('notificationUpdate', {
            detail: {
                count,
                timestamp: new Date().toISOString()
            }
        });
        window.dispatchEvent(event);
    }

    private dispatchLoginNotificationEvent(): void {
        const event = new CustomEvent('loginNotification', {
            detail: {
                timestamp: new Date().toISOString(),
                message: 'Login notification received'
            }
        });
        window.dispatchEvent(event);
    }

    public setPollingInterval(interval: number): void {
        this.pollingInterval = interval;
    }

    public getPollingStatus(): boolean {
        return this.isPolling;
    }

    public getNotificationStats(): NotificationStats | null {
        return this.notificationStats;
    }

    public async requestNotificationPermission(): Promise<boolean> {
        return await this.browserNotificationService.requestPermission();
    }

    public isNotificationSupported(): boolean {
        return this.browserNotificationService.isSupported;
    }

    public isPermissionGranted(): boolean {
        return this.browserNotificationService.isPermissionGranted();
    }

    public isOnlineStatus(): boolean {
        return this.isOnline;
    }

    public getRetryCount(): number {
        return this.retryCount;
    }

    // Method to manually refresh notifications
    public async refreshNotifications(): Promise<void> {
        if (this.isPolling) {
            await this.fetchAndProcessNotifications();
        }
    }

    // Method to clear notification cache
    public clearNotificationCache(): void {
        this.lastNotificationIds.clear();
        this.notificationStats = null;
        this.lastFetchedNotifications = [];
    }

    /** Last notifications list from the latest poll (avoids duplicate fetches in dropdown) */
    public getLastFetchedNotifications(): Notification[] {
        return this.lastFetchedNotifications || [];
    }
}

export default NotificationPollingService; 